/**
 * The canonical "you have unsaved changes" confirmation (BL-914, AC 5).
 *
 * Before this component the same dialog existed in four places with three
 * different wordings and two different button casings ("Discard unsaved
 * changes?" / "Discard changes" on the sidebar + auction settings vs
 * "Unsaved Changes" / "Discard Changes" on the invoice + settlement modals).
 * Binding the copy here makes the strings unforkable: a consumer can only
 * choose *whether* to show it, never *what it says* — apart from the title,
 * which a record page's draft discard words as "Discard this draft?" (BL-2521).
 *
 * Per ADR 030 § 3c the destructive-confirm job belongs to `ConfirmDialog` —
 * this is a copy binding over that primitive, not a fork of it.
 */
'use client';

import ConfirmDialog from './ConfirmDialog';

/**
 * The one canonical copy set. Exported so tests (and any future surface that
 * needs the strings without the component) assert against a single source.
 */
export const UNSAVED_CHANGES_COPY = {
  title: 'Discard unsaved changes?',
  message: 'You have unsaved changes. Leaving will discard them.',
  confirmText: 'Discard changes',
  cancelText: 'Cancel',
} as const;

/**
 * Props for {@link UnsavedChangesDialog}.
 */
export interface UnsavedChangesDialogProps {
  /** Whether the dialog is visible */
  isOpen: boolean;
  /** Called when the user keeps editing (cancel / Escape / backdrop) */
  onClose: () => void;
  /** Called when the user confirms discarding their edits */
  onConfirm: () => void | Promise<void>;
  /**
   * Optional extra sentence appended to the canonical message, for surfaces
   * that need to name *what* is being left (e.g. "Changes to this auction will
   * be lost."). It must be a whole sentence: it follows the canonical full stop,
   * so a fragment reads as broken copy (BUG-1804). The canonical sentences are
   * always present — this only adds context.
   */
  contextNote?: string;
  /** z-index — raise it when the dialog stacks above an already-open modal */
  zIndex?: number;
  /** Automation-handle namespace, passed to `ConfirmDialog` (`<testId>`, `-confirm`, `-cancel`). Nothing when unset. */
  testId?: string;
  /** Busy while the discard runs; passed to `ConfirmDialog` (BL-2310: the invoice edit bar's server discard). */
  isLoading?: boolean;
  /** The title, when the surface discards a whole draft (BL-2521). Defaults to the canonical title. */
  title?: string;
}

/**
 * Confirmation shown when a user navigates away from a form with pending edits.
 *
 * @param props - Visibility, the keep-editing and discard callbacks, and optional context.
 * @returns A `ConfirmDialog` bound to the canonical unsaved-changes copy.
 */
export default function UnsavedChangesDialog({
  isOpen,
  onClose,
  onConfirm,
  contextNote,
  zIndex,
  testId,
  isLoading,
  title = UNSAVED_CHANGES_COPY.title,
}: UnsavedChangesDialogProps) {
  const message = contextNote
    ? `${UNSAVED_CHANGES_COPY.message} ${contextNote}`
    : UNSAVED_CHANGES_COPY.message;

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title={title}
      message={message}
      confirmText={UNSAVED_CHANGES_COPY.confirmText}
      cancelText={UNSAVED_CHANGES_COPY.cancelText}
      variant="danger"
      zIndex={zIndex}
      testId={testId}
      isLoading={isLoading}
    />
  );
}
